/**
 * <carver-landing> — signup behaviour for sections/carver-landing.liquid.
 *
 * Initial state (which photo, signed up or not) is set by the section's inline
 * script before first paint. This module handles the form: it posts to Klaviyo's
 * client subscriptions API, then fades the form out and grows the small CARVER
 * mark into the CARVER BOOKS lockup. The photo stays put.
 */

const KEY_SUBSCRIBED = 'carver:subscribed';
const KLAVIYO_REVISION = '2025-01-15';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function store(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the page still works, it just won't remember.
  }
}

/**
 * Tell Klaviyo's onsite script (the Shopify app embed) who this visitor is, so
 * their browsing is tied to the profile the signup just created. Best effort: the
 * embed may be off or blocked, and the signup has already succeeded.
 * @param {{ email: string, first_name: string, last_name: string }} profile
 */
function identify({ email, first_name, last_name }) {
  const properties = { email };
  if (first_name) properties.first_name = first_name;
  if (last_name) properties.last_name = last_name;
  try {
    window.klaviyo?.identify(properties)?.catch?.(() => {});
  } catch {
    // Ignore: identification is optional.
  }
}

class CarverLanding extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;

    // Re-measure the page height only when the width changes (rotation, window resize).
    // Height-only changes are an on-screen keyboard or browser toolbars; ignoring them
    // keeps the photo and layout from resizing while someone types.
    let width = window.innerWidth;
    this.style.setProperty('--landing-height', `${window.innerHeight}px`);
    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth === width) return;
        width = window.innerWidth;
        this.style.setProperty('--landing-height', `${window.innerHeight}px`);
      },
      { signal }
    );
    this.form?.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    this.email?.addEventListener('input', () => this.#clearError(), { signal });

    if (this.hasAttribute('data-design-mode')) {
      // The editor may re-render the section without re-running its inline script.
      this.dataset.state = this.dataset.preview === 'subscribed' ? 'subscribed' : 'form';
      document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), { signal });
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get form() {
    return this.querySelector('.landing__form');
  }

  get email() {
    return this.querySelector('input[name="email"]');
  }

  get backgrounds() {
    return [...this.querySelectorAll('.landing__bg')];
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this.#busy) return;

    const data = new FormData(this.form);
    const email = String(data.get('email') || '').trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.#showError(this.dataset.invalidMessage);
      this.email?.focus();
      return;
    }

    this.#busy = true;
    this.#setPending(true);
    this.#clearError();

    const designMode = this.hasAttribute('data-design-mode');

    try {
      // The theme editor runs the success flow without calling Klaviyo.
      if (!designMode) {
        const profile = {
          email,
          first_name: String(data.get('first_name') || '').trim(),
          last_name: String(data.get('last_name') || '').trim(),
        };
        await this.#subscribe(profile);
        store(KEY_SUBSCRIBED, '1');
        identify(profile);
      }
      await this.#celebrate();
    } catch (error) {
      console.error('[carver-landing] Signup failed', error);
      this.#showError(this.dataset.errorMessage);
      this.#setPending(false);
    } finally {
      this.#busy = false;
    }
  }

  async #subscribe({ email, first_name, last_name }) {
    const { klaviyoKey, klaviyoList } = this.dataset;
    if (!klaviyoKey || !klaviyoList) {
      throw new Error('Klaviyo public key or list ID is not set in the theme editor.');
    }

    const attributes = { email };
    if (first_name) attributes.first_name = first_name;
    if (last_name) attributes.last_name = last_name;
    attributes.subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };

    const url = new URL('https://a.klaviyo.com/client/subscriptions');
    url.searchParams.set('company_id', klaviyoKey);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Carver landing page',
            profile: { data: { type: 'profile', attributes } },
          },
          relationships: { list: { data: { type: 'list', id: klaviyoList } } },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Klaviyo responded ${response.status}: ${await response.text()}`);
    }
  }

  async #celebrate() {
    const motion = !reducedMotion();

    this.dataset.state = 'leaving';
    if (motion) await wait(400);

    // Measure the small logotype before the layout switches to the large one.
    const small = this.querySelector('.wordmark--small');
    const first = small?.getBoundingClientRect();
    const lockup = small?.classList.contains('wordmark--lockup') ?? false;

    this.dataset.state = 'subscribed';

    if (motion && first) this.#growWordmark(first, lockup);
    this.#announceSuccess();
  }

  /**
   * FLIP: start the large logotype where the small one was, then let it grow into
   * place. A small CARVER BOOKS lockup is an exact scaled copy of the large one, so
   * it grows as one unit; a small CARVER alone grows while BOOKS fades in.
   * @param {DOMRect} first - The small logotype's rect before the switch.
   * @param {boolean} lockup - Whether the small logotype is the full lockup.
   */
  #growWordmark(first, lockup) {
    const large = this.querySelector('.wordmark--large');
    const carver = large?.querySelector('.wordmark__carver');
    const books = large?.querySelector('.wordmark__books');
    if (!large || !carver) return;

    // Near-linear with a slight settle at the end. Everything finishes together.
    const duration = 700;
    const booksDelay = 290;
    const easing = 'cubic-bezier(0.25, 0.25, 0.6, 1)';

    const grow = (element, from) => {
      const to = element.getBoundingClientRect();
      const scale = from.width / to.width;
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      element.animate(
        [{ transform: `translate(${dx}px, ${dy}px) scale(${scale})` }, { transform: 'none' }],
        { duration, easing }
      );
    };

    if (lockup) {
      grow(large, first);
      return;
    }

    grow(carver, first);
    books?.animate(
      [
        { opacity: 0, transform: 'translateY(12%)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: duration - booksDelay, delay: booksDelay, easing, fill: 'backwards' }
    );
  }

  /** Theme editor only: show the photo of the block being edited. */
  #showBackground(index) {
    const backgrounds = this.backgrounds;
    const incoming = backgrounds[index];
    if (!incoming || incoming.classList.contains('is-active')) return;

    const outgoing = backgrounds.find((bg) => bg.classList.contains('is-active'));
    const img = incoming.querySelector('img');
    if (img) img.loading = 'eager';

    this.dataset.tone = incoming.dataset.tone || 'light';
    incoming.classList.add('is-active');
    outgoing?.classList.remove('is-active');
  }

  #onBlockSelect(event) {
    const index = this.backgrounds.findIndex((bg) => bg.contains(event.target));
    if (index >= 0) this.#showBackground(index);
  }

  #setPending(pending) {
    const button = this.querySelector('.landing__submit');
    if (!button) return;
    button.setAttribute('aria-disabled', String(pending));
  }

  #showError(message) {
    const error = this.querySelector('.landing__error');
    if (error) error.textContent = message || '';
    this.email?.setAttribute('aria-invalid', 'true');
  }

  #clearError() {
    const error = this.querySelector('.landing__error');
    if (error) error.textContent = '';
    this.email?.removeAttribute('aria-invalid');
  }

  #announceSuccess() {
    const region = this.querySelector('.landing__success');
    const text = this.querySelector('.landing__success-text')?.content.textContent.trim();
    if (!region) return;
    region.textContent = text || '';
    region.focus({ preventScroll: true });
  }
}

if (!customElements.get('carver-landing')) {
  customElements.define('carver-landing', CarverLanding);
}
